import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material'
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  VisibilityOutlined,
  VisibilityOffOutlined,
} from '@mui/icons-material'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import BaseDataTable, { TableColumn } from '../../components/shared/BaseDataTable'
import ReportFilterCard from '../../components/shared/filters/ReportFilterCard'
import { useReportFilters } from '../../components/shared/filters/useReportFilters'
import { FilterKey } from '../../components/shared/filters/types'
import { useListPageState } from '../../hooks/useListPageState'
import { useDataFetching } from '../../hooks/useDataFetching'
import {
  getDomainMeta,
  getBusinessAreaMeta,
  ExceptionDomainIcon,
} from '../../components/SystemIcons'
import ExceptionTypeLabel from '../../components/shared/ExceptionTypeLabel'
import { poolwerxColors } from '../../theme/colors'
import { useNotify } from '../../components/shared/ToastProvider'
import {
  exceptionTypesApi,
  mapFiltersToParams,
  ExceptionType,
  ExceptionTypesResponse,
} from '../../services/admin/exceptionTypesApi'
import AudiencePresetChips from '../../components/admin/exception-types/AudiencePresetChips'

const ENABLED_FILTERS: FilterKey[] = ['textSearch', 'exceptionType', 'businessArea', 'dataDomain', 'market', 'visibleToRole']

type RoleChipColor = 'error' | 'primary' | 'success' | 'default'

interface RoleMeta {
  label: string
  short: string
  color: RoleChipColor
}

const ROLE_META: Record<string, RoleMeta> = {
  EVO_ADMIN: { label: 'Evo Admin', short: 'EVO', color: 'error' },
  EVO_TEAM: { label: 'Evo Team', short: 'TEAM', color: 'primary' },
  PSC_TEAM: { label: 'PSC Team', short: 'PSC', color: 'success' },
  FP_USER: { label: 'FP User', short: 'FP', color: 'default' },
}

const ROLE_ORDER = Object.keys(ROLE_META)

type FpVisibilityFilter = 'all' | 'fp_visible' | 'internal'

const ExceptionSetup: React.FC = () => {
  const [isActive, setIsActive] = useState<string>('true')
  const [fpVisibility, setFpVisibility] = useState<FpVisibilityFilter>('all')
  const [exporting, setExporting] = useState(false)
  const { showSuccess, showError } = useNotify()

  const { filters, updateFilter, clearFilters, hasActiveFilters } = useReportFilters({
    enabledFilters: ENABLED_FILTERS,
    // useListPageState owns the URL on this page (matches Users.tsx). Two
    // URL-syncing hooks fought and reverted row-link navigations to the edit page.
    syncToUrl: false,
  })

  const { state, updateField } = useListPageState({
    defaultSortField: 'data_domain',
    defaultPerPage: 25,
  })

  const filterSignature = `${filters.textSearch || ''}|${filters.exceptionType || ''}|${filters.businessArea || ''}|${filters.dataDomain || ''}|${filters.market || ''}|${filters.visibleToRole || ''}|${isActive}|${fpVisibility}`

  useEffect(() => {
    if (state.page !== 1) {
      updateField('page', 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSignature])

  const queryParams = useMemo(
    () => mapFiltersToParams(filters, isActive, state.page, state.perPage),
    [filters, isActive, state.page, state.perPage],
  )

  const fetchExceptionTypes = useCallback(
    () => exceptionTypesApi.list(queryParams),
    [queryParams],
  )

  const { data, loading, error } = useDataFetching<ExceptionTypesResponse>({
    fetchFn: fetchExceptionTypes,
    dependencies: [queryParams],
  })

  const handleApplyPreset = (market: string, visibleToRole: string) => {
    updateFilter('market', market)
    updateFilter('visibleToRole', visibleToRole)
  }

  const handleClearFilters = () => {
    clearFilters()
    setIsActive('true')
    setFpVisibility('all')
  }

  const handleStatusChange = (event: SelectChangeEvent) => {
    setIsActive(event.target.value)
  }

  const handleFpVisibilityChange = (event: SelectChangeEvent) => {
    setFpVisibility(event.target.value as FpVisibilityFilter)
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      await exceptionTypesApi.export(filters, isActive)
      showSuccess('Export downloaded successfully')
    } catch (err) {
      console.error('Export failed:', err)
      showError('Failed to export exception types')
    } finally {
      setExporting(false)
    }
  }

  const isFpVisible = (et: ExceptionType): boolean =>
    (et.visible_to_roles ?? []).includes('FP_USER')

  const sortedRows = useMemo(() => {
    let rows = data?.exception_types ?? []
    if (fpVisibility !== 'all') {
      rows = rows.filter((et) =>
        fpVisibility === 'fp_visible' ? isFpVisible(et) : !isFpVisible(et),
      )
    }
    if (!state.sortField) return rows
    const direction = state.sortDirection === 'desc' ? -1 : 1
    return [...rows].sort((a, b) => {
      // 'links' sorts by help-link count; everything else by the row field.
      const aVal =
        state.sortField === 'links'
          ? a.links?.length ?? 0
          : (a as any)[state.sortField] ?? ''
      const bVal =
        state.sortField === 'links'
          ? b.links?.length ?? 0
          : (b as any)[state.sortField] ?? ''
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * direction
      }
      return String(aVal).localeCompare(String(bVal)) * direction
    })
  }, [data, fpVisibility, state.sortField, state.sortDirection])

  const columns: TableColumn<ExceptionType>[] = [
    {
      key: 'data_domain',
      label: 'Data Domain',
      sortable: true,
      width: 190,
      render: (et) => (
        <Chip
          icon={
            <ExceptionDomainIcon domain={et.data_domain} variant="outlined" size={15} />
          }
          label={getDomainMeta(et.data_domain).label}
          size="small"
          variant="outlined"
          sx={{
            height: 24,
            pl: '6px', // root padding wins over MUI's .MuiChip-iconSmall margin
            fontWeight: 500,
            color: 'text.secondary',
            borderColor: 'divider',
            backgroundColor: 'grey.100',
            '& .MuiChip-icon': { color: 'inherit', fontSize: 15 },
          }}
        />
      ),
    },
    {
      key: 'name',
      label: 'Exception',
      sortable: true,
      render: (et) => (
        <Typography
          component={RouterLink}
          to={`/admin/system/exception-setup/${et.id}/edit`}
          variant="body2"
          fontWeight={500}
          sx={{
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {et.name}
        </Typography>
      ),
    },
    {
      key: 'exception_type',
      label: 'Classification',
      sortable: true,
      width: 230,
      render: (et) => {
        // Business Area carries the action-owner sense (the dept that acts on
        // this); Data Domain now has its own column.
        const context = `Actioned by ${getBusinessAreaMeta(et.business_area).label}`
        return (
          <Stack spacing={0.5} alignItems="flex-start">
            <ExceptionTypeLabel
              variant="pill"
              exceptionType={et.exception_type}
              iconSize={15}
            />
            <Typography variant="caption" color="text.secondary">
              {context}
            </Typography>
          </Stack>
        )
      },
    },
    {
      key: 'fp_visibility',
      label: 'Scope / Visibility',
      sortable: false,
      width: 230,
      render: (et) => {
        const visible = isFpVisible(et)
        const Icon = visible ? VisibilityOutlined : VisibilityOffOutlined
        const markets = et.markets ?? []
        const orderedRoles = ROLE_ORDER.filter((r) =>
          (et.visible_to_roles ?? []).includes(r),
        )
        const marketSummary = markets.length > 0 ? markets.join(', ') : 'All markets'
        const roleSummary =
          orderedRoles.length > 0
            ? orderedRoles.map((r) => ROLE_META[r]?.short || r).join(', ')
            : 'Hidden'
        return (
          <Stack spacing={0.5} alignItems="flex-start">
            <Chip
              icon={<Icon sx={{ fontSize: 14 }} />}
              label={visible ? 'FP Visible' : 'PSC Only'}
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 500,
                borderColor: visible ? 'rgba(0,127,152,0.4)' : 'divider',
                color: visible ? poolwerxColors.teal : 'text.secondary',
                '& .MuiChip-icon': { color: 'inherit', marginLeft: '6px' },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {marketSummary} · {roleSummary}
            </Typography>
          </Stack>
        )
      },
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 100,
      render: (et) => (
        <Chip
          label={et.is_active ? 'Active' : 'Inactive'}
          size="small"
          color={et.is_active ? 'success' : 'default'}
          variant={et.is_active ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      key: 'links',
      label: 'Links',
      sortable: true,
      align: 'center',
      width: 90,
      render: (et) => {
        const count = et.links?.length ?? 0
        return (
          <Tooltip
            title={count > 0 ? 'View help & documentation' : 'Add help & documentation'}
          >
            <Stack
              component={RouterLink}
              to={`/admin/system/exception-setup/${et.id}/edit#documentation`}
              direction="row"
              spacing={0.5}
              alignItems="center"
              justifyContent="center"
              sx={{
                textDecoration: 'none',
                color: count > 0 ? 'primary.main' : 'text.secondary',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <LinkIcon sx={{ fontSize: 15 }} />
              <Typography variant="body2" fontWeight={500}>
                {count}
              </Typography>
            </Stack>
          </Tooltip>
        )
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      width: 80,
      render: (et) => (
        <Tooltip title="Edit">
          <IconButton
            component={RouterLink}
            to={`/admin/system/exception-setup/${et.id}/edit`}
            size="small"
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  const total = data?.total ?? 0

  return (
    <>
      <AdminPageHeader
        pageKey="exceptionTypes"
        countLabel={loading ? undefined : `${total.toLocaleString()} exceptions`}
        actions={
          <Button
            component={RouterLink}
            to="/admin/system/exception-setup/new"
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: poolwerxColors.teal,
              '&:hover': { backgroundColor: poolwerxColors.tealHover },
            }}
          >
            Add Exception
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          mb: 2,
          p: 2,
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <AudiencePresetChips filters={filters} onApplyPreset={handleApplyPreset} />
      </Box>

      <ReportFilterCard
        filters={filters}
        onFilterChange={updateFilter}
        onClearFilters={handleClearFilters}
        enabledFilters={ENABLED_FILTERS}
        hasActiveFilters={hasActiveFilters || isActive !== 'true' || fpVisibility !== 'all'}
        loading={loading}
        exportButton={
          <Button
            variant="outlined"
            size="small"
            onClick={handleExport}
            disabled={exporting}
            startIcon={<DownloadIcon />}
            sx={{
              borderColor: '#E6E8EA',
              color: '#1F2A2E',
              px: 2,
              '&:hover': {
                borderColor: poolwerxColors.teal,
                backgroundColor: '#E6F4F5',
              },
            }}
          >
            Export Excel
          </Button>
        }
        extraFilters={
          <Stack direction="row" spacing={1.5}>
            <Box sx={{ minWidth: 140 }}>
              <Typography
                component="label"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  mb: 0.5,
                  display: 'block',
                }}
              >
                Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select displayEmpty value={isActive} onChange={handleStatusChange}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: 160 }}>
              <Typography
                component="label"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  mb: 0.5,
                  display: 'block',
                }}
              >
                FP visibility
              </Typography>
              <FormControl fullWidth size="small">
                <Select value={fpVisibility} onChange={handleFpVisibilityChange}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="fp_visible">FP Visible</MenuItem>
                  <MenuItem value="internal">PSC Only</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        }
      />

      <BaseDataTable<ExceptionType>
        variant="enhanced"
        columns={columns}
        data={sortedRows}
        loading={loading}
        emptyMessage="No exceptions found"
        sortField={state.sortField}
        sortDirection={state.sortDirection}
        onSort={(field, direction) => {
          updateField('sortField', field)
          updateField('sortDirection', direction)
        }}
        showCount
        countLabel="exceptions"
        totalCount={total}
        pagination={{
          page: state.page,
          perPage: state.perPage,
          total,
          onPageChange: (p) => updateField('page', p),
        }}
        getRowKey={(et) => et.id}
      />
    </>
  )
}

export default ExceptionSetup
